import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import LinkIconButton from '@atlaskit/button/icon/link';
import { cssMap, cx } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import DepartmentIcon from '@atlaskit/icon/core/department';
import { Inline } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics/use-analytics-events';

import { getIconForExternalMessageIntegration } from '../utils/externalMessageIntegrationIcons';
import { resolveExternalMessageFromIntegrations } from '../utils/thirdPartyIntegrationsByProduct';

const messages = defineMessages({
	viewOrgChartLabel: {
		id: 'user-inline-preview.action-buttons.view-org-chart-label',
		defaultMessage: 'View the reporting line for {name}',
		description:
			'Accessible label for the org chart icon button. The {name} placeholder is the name of the user.',
	},
	sendMessageLabel: {
		id: 'user-inline-preview.action-buttons.send-message-label',
		defaultMessage: 'Message {name}',
		description:
			'Accessible label for the send message button (e.g. Slack or Microsoft Teams). The {name} placeholder is the name of the user.',
	},
	viewProfileLabel: {
		id: 'user-inline-preview.action-buttons.view-profile-label',
		defaultMessage: 'View the profile for {name}',
		description:
			'Accessible label for the navigate to profile button. The {name} placeholder is the name of the user.',
	},
	sendMessageTooltip: {
		id: 'user-inline-preview.action-buttons.send-message-tooltip',
		defaultMessage: 'Message',
		description: 'Accessible label for the send message tooltip (e.g. Slack or Microsoft Teams)',
	},
	viewOrgChartTooltip: {
		id: 'user-inline-preview.action-buttons.view-org-chart-tooltip',
		defaultMessage: 'Reporting line',
		description: 'Accessible label for the reporting line tooltip.',
	},
	viewProfileTooltip: {
		id: 'user-inline-preview.action-buttons.view-profile-tooltip',
		defaultMessage: 'View profile',
		description: 'Accessible label for the view profile tooltip.',
	},
});

const styles = cssMap({
	actions: {
		opacity: 0,
	},
	actionsVisible: {
		opacity: 1,
	},
});

export interface ActionButtonsProps {
	name: string;
	accountId: string;
	email?: string | null;
	isVisible: boolean;
	cloudId: string;
	onViewOrgChart?: () => void;
	thirdPartyIntegrations?: {
		externalId: string;
		integrationId: string;
		product: string;
	}[];
	/**
	 * Optional context describing where this UserInlinePreview is rendered. Included as an
	 * attribute in native analytics events so consumers can distinguish e.g. a collaborator
	 * click inside a UserProfilePreview from a standalone UserInlinePreview click.
	 * Example: `'collaborator'`
	 */
	renderContext?: string;
}

/**
 * Internal component that renders action buttons (3p message, org chart, view profile).
 * Shows each button when the corresponding data and handler are available.
 */
function ActionButtons({
	name,
	accountId,
	email,
	isVisible,
	cloudId,
	onViewOrgChart,
	thirdPartyIntegrations,
	renderContext,
}: ActionButtonsProps): React.JSX.Element {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const { href, target, onNavigate } = navigateToTeamsApp({
		type: 'USER',
		payload: { userId: accountId },
		cloudId: cloudId ?? '',
	});

	const externalMessage = useMemo(
		() => resolveExternalMessageFromIntegrations(thirdPartyIntegrations, email),
		[email, thirdPartyIntegrations],
	);

	const handleThirdPartyMessageClick = useCallback(
		(event: React.MouseEvent) => {
			if (event.type === 'auxclick' && event.button !== 1) {
				return;
			}
			fireEvent('ui.userInlinePreview.clicked.thirdPartyMessage', {
				integration: externalMessage?.entry.integrationId ?? '',
				isAuxClick: event.type === 'auxclick',
				...(renderContext ? { renderContext } : {}),
			});
		},
		[fireEvent, externalMessage, renderContext],
	);

	const handleOrgChartClick = useCallback(
		(event: React.MouseEvent) => {
			if (event.type === 'auxclick' && event.button !== 1) {
				return;
			}
			fireEvent('ui.userInlinePreview.clicked.reportingLines', {
				isAuxClick: event.type === 'auxclick',
				...(renderContext ? { renderContext } : {}),
			});
			onViewOrgChart?.();
		},
		[fireEvent, onViewOrgChart, renderContext],
	);

	const handleViewProfileClick = useCallback(
		(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			if (event.type === 'auxclick' && event.button !== 1) {
				return;
			}
			const auxClick = event.type === 'auxclick';
			fireEvent('ui.userInlinePreview.clicked.viewProfile', {
				isAuxClick: auxClick,
				...(renderContext ? { renderContext } : {}),
			});
			// A middle-click on a link opens a new tab natively, so only run SPA navigation for
			// primary clicks.
			if (!auxClick) {
				onNavigate?.(event);
			}
		},
		[fireEvent, onNavigate, renderContext],
	);

	/** When actions are visually hidden, keep them out of the tab order so `aria-hidden` does not trap focus (axe aria-hidden-focus). */
	const tabIndexWhenHidden = isVisible ? undefined : -1;

	// TODO update org chart to use the TeamsLinkIconButton (once available) — see UserProfilePreview ActionBar
	return (
		<Inline
			space="space.100"
			alignBlock="center"
			aria-hidden={!isVisible}
			xcss={cx(styles.actions, isVisible && styles.actionsVisible)}
		>
			{externalMessage && (
				<LinkIconButton
					tabIndex={tabIndexWhenHidden}
					href={externalMessage.dm.openDmUrl}
					target="_blank"
					rel="noopener noreferrer"
					onClick={handleThirdPartyMessageClick}
					onAuxClick={handleThirdPartyMessageClick}
					icon={getIconForExternalMessageIntegration(externalMessage.dm.integration)}
					label={intl.formatMessage(messages.sendMessageLabel, { name })}
					isTooltipDisabled={false}
					tooltip={{ content: intl.formatMessage(messages.sendMessageTooltip) }}
				/>
			)}
			{accountId && onViewOrgChart && (
				<IconButton
					tabIndex={tabIndexWhenHidden}
					onClick={handleOrgChartClick}
					onAuxClick={handleOrgChartClick}
					icon={DepartmentIcon}
					label={intl.formatMessage(messages.viewOrgChartLabel, { name })}
					isTooltipDisabled={false}
					tooltip={{ content: intl.formatMessage(messages.viewOrgChartTooltip) }}
				/>
			)}
			{accountId && href && (
				<LinkIconButton
					tabIndex={tabIndexWhenHidden}
					href={href}
					target={target}
					onClick={handleViewProfileClick}
					onAuxClick={handleViewProfileClick}
					icon={ArrowRightIcon}
					label={intl.formatMessage(messages.viewProfileLabel, { name })}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
					isTooltipDisabled={false}
					tooltip={{ content: intl.formatMessage(messages.viewProfileTooltip) }}
				/>
			)}
		</Inline>
	);
}

export { ActionButtons };
